"use client";

import { Play, Users, Eye, ExternalLink, Calendar } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState } from "react";

import { LoggerFactory } from "@/shared/lib/logger";
import { Button } from "@/shared/ui/buttons/button";
import { Card } from "@/shared/ui/cards/card";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { Badge } from "@/shared/ui/data-display/badge";
import { TwitchEmbed } from "./twitch-embed";
import { formatDateOnlyMsk } from "@/shared/lib/msk-time";

// Individual Twitch stream card
interface TwitchStreamCardProps {
  stream: {
    id: string;
    title: string;
    streamer: string;
    streamerAvatar: string;
    thumbnail: string;
    url: string;
    viewers: number;
    duration: string;
    startTime: string;
    platform: "twitch" | "youtube" | "vimeo";
    isLive: boolean;
  };
  className?: string;
}

export function TwitchStreamCard({
  stream,
  className = "",
}: TwitchStreamCardProps) {
  const tUi = useTranslations("Ui");
  const [isPlaying, setIsPlaying] = useState(false);
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [useEmbed, setUseEmbed] = useState(true);
  const [volume] = useState(0.8);

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case "twitch":
        return "bg-purple-100 text-purple-700";
      case "youtube":
        return "bg-red-100 text-red-700";
      case "vimeo":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const streamUrl = stream.url;
  const thumbnailUrl = stream.thumbnail;
  const avatarUrl = stream.streamerAvatar;

  const logger = LoggerFactory.forComponent("TwitchStreamCard", {
    streamer: stream.streamer,
    platform: "twitch",
  });

  const handleEmbedError = (error: string) => {
    logger.error("Twitch embed error", new Error(error), {
      streamer: stream.streamer,
      embed_error: error,
      use_embed: useEmbed,
    });
    setPlayerError(error);
    setUseEmbed(false);
  };

  const handleEmbedLoad = () => {
    logger.info("Twitch embed loaded successfully", {
      streamer: stream.streamer,
      load_time: Date.now(),
    });
    setPlayerError(null);
  };

  // Debug info with proper logging
  logger.debug("Stream data rendered", {
    streamer: stream.streamer,
    stream_url: streamUrl,
    thumbnail_url: thumbnailUrl,
    is_playing: isPlaying,
    has_embed_error: !!playerError,
  });

  return (
    <Card className={`overflow-hidden ${className}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
        {/* Left side - Information */}
        <div className="lg:col-span-1 p-6 flex flex-col justify-between">
          <div className="space-y-4">
            {/* Title and badges */}
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <h3 className="font-semibold text-lg leading-tight">
                  {stream.title}
                </h3>
                <Badge className="bg-red-500 text-white animate-pulse text-xs">
                  LIVE
                </Badge>
              </div>
              <div className="flex gap-2">
                <Badge className={getPlatformColor("twitch")}>TWITCH</Badge>
                <Badge variant="secondary">
                  {stream.platform.toUpperCase()}
                </Badge>
              </div>
            </div>

            {/* Streamer Info */}
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={avatarUrl} alt={stream.streamer} />
                <AvatarFallback>
                  <Users className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{stream.streamer}</p>
                <p className="text-sm text-muted-foreground">{tUi("TwitchStreamCard.strimer")}</p>
              </div>
            </div>

            {/* Stats */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Eye className="h-4 w-4 text-red-500" />
                <span className="font-medium">
                  {stream.viewers.toLocaleString()} зрителей
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>
                  Начат: {formatDateOnlyMsk(new Date(stream.startTime))}
                </span>
              </div>
            </div>
          </div>

          {/* External Link */}
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-4"
            onClick={() => window.open(streamUrl, "_blank")}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {tUi("TwitchStreamCard.otkrytVTwitch")}
          </Button>
        </div>

        {/* Right side - Video Player */}
        <div className="lg:col-span-2">
          <div className="aspect-video bg-gray-900 relative">
            {!isPlaying ? (
              <>
                <Image
                  src={thumbnailUrl}
                  alt={stream.title}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Button
                    size="lg"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                    onClick={() => setIsPlaying(true)}
                  >
                    <Play className="h-8 w-8 mr-3" />
                    {tUi("TwitchStreamCard.smotretLive")}
                  </Button>
                </div>
              </>
            ) : playerError ? (
              <div className="flex items-center justify-center h-full bg-gray-900 text-white">
                <div className="text-center">
                  <p className="text-red-400 mb-2">{tUi("TwitchStreamCard.oshibkaVosproizvedeniya")}</p>
                  <p className="text-sm text-gray-400 mb-4">{playerError}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPlayerError(null);
                      setIsPlaying(false);
                    }}
                  >
                    {tUi("TwitchStreamCard.poprobovatSnova")}
                  </Button>
                </div>
              </div>
            ) : useEmbed ? (
              <TwitchEmbed
                channel={stream.url.split("/").pop() || "twitch"}
                width="100%"
                height="100%"
                autoplay={isPlaying}
                muted={volume === 0}
                quality="chunked"
                theme="dark"
                layout="video"
                allowFullscreen={true}
                onError={handleEmbedError}
                onLoad={handleEmbedLoad}
                onReady={() => {}}
                onPlay={() => {}}
                onPause={() => {}}
                onEnded={() => {}}
              />
            ) : (
              <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="mb-4">
                    <div className="w-16 h-16 mx-auto mb-4 bg-purple-600 rounded-full flex items-center justify-center">
                      <Play className="h-8 w-8" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">
                      Стрим {stream.streamer}
                    </h3>
                    <p className="text-gray-400 mb-4">
                      {tUi("TwitchStreamCard.nazhmiteDlyaProsmotraNa")}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Button
                      onClick={() => window.open(streamUrl, "_blank")}
                      className="bg-purple-600 hover:bg-purple-700 w-full"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {tUi("TwitchStreamCard.otkrytVTwitch")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setUseEmbed(true);
                        setPlayerError(null);
                      }}
                      className="w-full"
                    >
                      {tUi("TwitchStreamCard.poprobovatVstroit")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
